import { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
  Dropdown,
  Modal,
} from "react-bootstrap";
import { useNavigate, Navigate } from "react-router-dom";
import LoginModal from "../components/LoginModal";
import { useAuth } from "../auth/AuthContext";
import "../css/profile.css";
import { API_BASE_URL } from "../config";

export default function Profile() {
  const navigate = useNavigate();
  const { isAuthenticated, user: authUser, token, login, logout } = useAuth();

  const [checkoutStatus, setCheckoutStatus] = useState(null);

  const [user, setUser] = useState(authUser || null);
  const [editing, setEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(authUser || null);

  const [showLoginModal, setShowLoginModal] = useState(false);

  const [loadingExtras, setLoadingExtras] = useState(false);
  const [myWorkouts, setMyWorkouts] = useState([]);
  const [myCoaching, setMyCoaching] = useState(null);

  const [showPwd, setShowPwd] = useState(false);
  const [showDel, setShowDel] = useState(false);

  const [pwdForm, setPwdForm] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [pwdError, setPwdError] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);

  // ✅ Legge ?checkout=success da /profile e mostra il banner una sola volta
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("checkout");

    if (status === "success") {
      setCheckoutStatus("success");

      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Carica workouts e coaching dell'utente
  useEffect(() => {
    if (!isAuthenticated || !authUser?.id) return;

    (async () => {
      try {
        setUser(authUser);
        setEditedUser(authUser);
        setLoadingExtras(true);

        // Workouts assegnati all'utente
        const ownedRes = await fetch(
          `${API_BASE_URL}/api/users/${authUser.id}/workouts`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            credentials: "include",
          }
        );

        if (ownedRes.ok) {
          const list = await ownedRes.json();
          setMyWorkouts(Array.isArray(list) ? list : []);
        } else {
          // Fallback pubblico (solo anteprima)
          const pubRes = await fetch(`${API_BASE_URL}/api/workouts`);
          const pubList = pubRes.ok ? await pubRes.json() : [];

          setMyWorkouts(
            Array.isArray(pubList)
              ? pubList.map((w) => ({
                  id: w.id,
                  title: w.title,
                  weeks: w.weeks,
                }))
              : []
          );
        }

        // Coaching status dell'utente
        const csRes = await fetch(
          `${API_BASE_URL}/api/users/${authUser.id}/coaching`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            credentials: "include",
          }
        );
        if (csRes.ok) {
          const cs = await csRes.json();
          setMyCoaching(cs || null);
        } else {
          setMyCoaching(null);
        }
      } catch (e) {
        console.error("Profile load error:", e);
      } finally {
        setLoadingExtras(false);
      }
    })();
  }, [isAuthenticated, authUser, token]);

  const role = (authUser?.role || authUser?.ruolo || "").toLowerCase().trim();
  if (isAuthenticated && role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setEditedUser((s) => ({ ...s, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (!editedUser?.id) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${editedUser.id}`, {
        method: "PUT",
        credentials: "include", // ✅ manda JSESSIONID
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: editedUser.firstName,
          lastName: editedUser.lastName,
          username: editedUser.username,
          email: editedUser.email,
          city: editedUser.city ?? "",
          state: editedUser.state ?? "",
        }),
      });

      const txt = await res.text();
      const payload = txt ? JSON.parse(txt) : null;

      if (!res.ok) throw new Error(payload?.message || `HTTP ${res.status}`);

      setUser(payload);
      setEditedUser(payload);
      setEditing(false);
      login(payload, null); // niente JWT
    } catch (e) {
      console.error(e);
      alert(e.message || "Errore salvataggio profilo");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleDeleteProfile = async () => {
    if (!user?.id) return;
    const ok = window.confirm(
      "Are you sure you want to permanently delete your account? This action cannot be undone."
    );
    if (!ok) return;
    try {
      // TODO: DELETE /api/users/{id}
      await logout();
      navigate("/");
    } catch (e) {
      console.error("Profile deletion error:", e);
    }
  };

  async function handleChangePassword(e) {
    e?.preventDefault?.();
    setPwdError("");

    if (!pwdForm.current || !pwdForm.next || !pwdForm.confirm) {
      setPwdError("Please fill out all fields.");
      return;
    }
    if (pwdForm.next.length < 8) {
      setPwdError("New password must be at least 8 characters.");
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      setPwdError("Passwords do not match.");
      return;
    }
    if (!user?.id) {
      setPwdError("Invalid user.");
      return;
    }

    setPwdLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${user.id}/password`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          oldPassword: pwdForm.current,
          newPassword: pwdForm.next,
        }),
      });

      const txt = await res.text();
      const payload = txt ? JSON.parse(txt) : null;

      if (!res.ok) {
        throw new Error(payload?.message || `HTTP ${res.status}`);
      }

      setShowPwd(false);
      setPwdForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPwdError(err.message || "Password change failed.");
    } finally {
      setPwdLoading(false);
    }
  } // ✅ CHIUSURA FUNZIONE QUI

  const downloadPdf = async (workoutId) => {
    if (!user?.id) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/users/${user.id}/workouts/${workoutId}/pdf`,
        {
          method: "GET",
          credentials: "include",
          // se NON usi JWT, puoi togliere questo:
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      if (res.status === 401) return alert("Devi effettuare il login.");
      if (res.status === 403) return alert("Non possiedi questo workout.");
      if (!res.ok) return alert("PDF non disponibile.");

      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `workout-${workoutId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      console.error(e);
      alert("Errore durante il download del PDF.");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="errorLog">
        <div className="errorContent text-center">
          <p className="fw-bold fs-1">You're not logged in!</p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button variant="primary" onClick={() => setShowLoginModal(true)}>
              Sign in
            </Button>
            <Button variant="success" onClick={() => navigate("/register")}>
              Register
            </Button>
          </div>
        </div>

        <LoginModal
          show={showLoginModal}
          handleClose={() => setShowLoginModal(false)}
          onSuccess={() => setShowLoginModal(false)}
        />
      </div>
    );
  }

  if (!user) return <p>Loading profile...</p>;

  return (
    <div className="profile-container">
      {checkoutStatus === "success" && (
        <div className="alert alert-success text-center mb-3">
          Payment completed successfully. Your workout is now available in your
          profile.
        </div>
      )}

      <Container className="profile11">
        <Row className="justify-content-center">
          <Col md={10}>
            {/* Settings dropdown */}
            <div className="d-flex justify-content-end mb-3">
              <Dropdown align="end">
                <Dropdown.Toggle variant="secondary">Settings</Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setShowPwd(true)}>
                    Change password
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    className="text-danger"
                    onClick={() => setShowDel(true)}
                  >
                    Delete profile
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>

            {/* PERSONAL DATA */}
            <Card className="profile-card mb-4">
              <Card.Body className="cardbody">
                {editing ? (
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveProfile();
                    }}
                  >
                    <Form.Group controlId="formFirstName">
                      <Form.Label>First name</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={editedUser?.firstName || ""}
                        onChange={handleFieldChange}
                      />
                    </Form.Group>

                    <Form.Group controlId="formLastName" className="mt-3">
                      <Form.Label>Last name</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={editedUser?.lastName || ""}
                        onChange={handleFieldChange}
                      />
                    </Form.Group>

                    <Form.Group controlId="formUsername" className="mt-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        value={editedUser?.username || ""}
                        onChange={handleFieldChange}
                      />
                    </Form.Group>

                    <Form.Group controlId="formEmail" className="mt-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={editedUser?.email || ""}
                        onChange={handleFieldChange}
                      />
                    </Form.Group>

                    <div className="d-flex gap-2 mt-3">
                      <Button type="submit" className="profile-button">
                        Save changes
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setEditedUser(user);
                          setEditing(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                ) : (
                  <>
                    <h2>
                      {user?.firstName} {user?.lastName}
                    </h2>
                    <p>
                      <strong>Username:</strong> {user?.username}
                    </p>
                    <p>
                      <strong>Email:</strong> {user?.email}
                    </p>
                    <Button
                      className="profile-button"
                      onClick={() => setEditing(true)}
                    >
                      Edit Profile
                    </Button>
                  </>
                )}
              </Card.Body>
            </Card>

            {/* WORKOUTS */}
            <Card className="profile-card mb-4">
              <Card.Header as="h5">My Workouts</Card.Header>
              <Card.Body>
                {loadingExtras ? (
                  <Spinner animation="border" />
                ) : myWorkouts.length === 0 ? (
                  <p className="text-muted-soft">
                    No plans downloaded yet. Go to the Workouts section!
                  </p>
                ) : (
                  <ul>
                    {myWorkouts.map((w) => (
                      <li
                        key={w.id}
                        className="d-flex align-items-center justify-content-between py-1"
                      >
                        <div className="me-3">
                          <strong>{w.title}</strong>
                          {w.weeks ? <> — {w.weeks} weeks</> : null}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => downloadPdf(w.id)}
                          title="Scarica il PDF"
                        >
                          Download PDF
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </Card.Body>
            </Card>

            {/* COACHING */}
            <Card className="profile-card mb-3">
              <Card.Header as="h5">My Coaching</Card.Header>
              <Card.Body>
                {loadingExtras ? (
                  <Spinner animation="border" />
                ) : !myCoaching ? (
                  <p className="text-muted-soft">
                    No active plan. Go to Coaching!
                  </p>
                ) : (
                  <ul>
                    <li>
                      <strong>Package:</strong> {myCoaching.package}
                    </li>
                    <li>
                      <strong>Timeline:</strong> {myCoaching.timeline}
                    </li>
                    <li>
                      <strong>Status:</strong> {myCoaching.status}
                    </li>
                    <li>
                      <strong>Start:</strong> {myCoaching.startDate}
                    </li>
                  </ul>
                )}
              </Card.Body>
            </Card>

            {/* ACTIONS */}
            <Row className="justify-content-center">
              <Col md={6}>
                <div className="profile-actions">
                  <Button variant="danger" onClick={handleLogout}>
                    Logout
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* ===== Modals ===== */}

      <Modal show={showPwd} onHide={() => setShowPwd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleChangePassword}>
            <Form.Group className="mb-3">
              <Form.Label>Current password</Form.Label>
              <Form.Control
                type="password"
                value={pwdForm.current}
                onChange={(e) =>
                  setPwdForm((s) => ({ ...s, current: e.target.value }))
                }
                autoComplete="current-password"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>New password</Form.Label>
              <Form.Control
                type="password"
                minLength={8}
                value={pwdForm.next}
                onChange={(e) =>
                  setPwdForm((s) => ({ ...s, next: e.target.value }))
                }
                autoComplete="new-password"
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Confirm new password</Form.Label>
              <Form.Control
                type="password"
                minLength={8}
                value={pwdForm.confirm}
                onChange={(e) =>
                  setPwdForm((s) => ({ ...s, confirm: e.target.value }))
                }
                autoComplete="new-password"
                required
              />
            </Form.Group>
            {pwdError && <p className="text-danger mt-2">{pwdError}</p>}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPwd(false)}
            disabled={pwdLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleChangePassword}
            disabled={pwdLoading}
          >
            {pwdLoading ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDel} onHide={() => setShowDel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            This action is <strong>irreversible</strong>. Are you sure you want
            to delete your account?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDel(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setShowDel(false);
              handleDeleteProfile();
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
